/**
 * Fim de uma sessão de revisão: comemoração, quatro números, a distribuição
 * das notas, os próximos dias e os cartões para reforçar. Formata a
 * ReviewSessionSummary e os AgendaDia que a tela já leu. Não busca, não
 * chama agendaDePrevisao e não recalcula um total que essa leitura não trouxe.
 *
 * No estreito o corpo rola e as ações ficam no rodapé do viewport. Compartilhar
 * só entra na árvore quando `navigator.share` é função — lido a cada render.
 */
import { PartyPopper, Share, X } from "lucide-react";
import type { Rating, ReviewSessionSummary } from "../../../sdk/@types/Revisao";
import AgendaFita from "./AgendaFita";
import { ConclusaoAcoes } from "./ConclusaoAcoes";
import {
  contagemDeAmanha,
  listaDeReforco,
  type CartaoDeReforco,
} from "./conclusaoLeitura";
import type { AgendaDia } from "./home/revisaoHome";

export interface ConclusaoSessaoProps {
  summary: ReviewSessionSummary;
  /** Same AgendaDia[] AgendaFita renders. Display it. Do not recount, pad, or fetch. */
  dias: readonly AgendaDia[];
  /** subjectId → name. Omitted names fall through assuntoDoCartao ("Sem assunto"). */
  assuntos?: Readonly<Record<string, string>>;
  onVoltar: () => void;
  onAntecipar: () => void;
  onRevisarErros: () => void;
}

const UNAVAILABLE = "indisponível";
const MS_PER_MINUTE = 60_000;

const BOTAO_CABECALHO =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-edge bg-surface text-ink hover:bg-surface-hover";

const RATING_ROWS: readonly {
  key: Rating;
  label: string;
  ink: string;
  bar: string;
}[] = [
  { key: "again", label: "Errei", ink: "text-rate-again", bar: "bg-rate-again" },
  { key: "hard", label: "Difícil", ink: "text-rate-hard", bar: "bg-rate-hard" },
  { key: "good", label: "Bom", ink: "text-rate-good", bar: "bg-rate-good" },
  {
    key: "easy",
    label: "Fácil",
    // Lima como texto sobre superfície clara fica ~1.4:1 (revisaoSurfaces.ts).
    // No claro a tinta é on-lime; no escuro, rate-easy. A barra é rate-easy
    // nos dois. Trocar os dois para text-rate-easy apaga o 8 no tema claro.
    ink: "text-on-lime dark:text-rate-easy",
    bar: "bg-rate-easy",
  },
];

function isPresentNumber(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** One decimal in pt-BR (`82,5`). The magnitude is shown as given — not rescaled. */
function umaCasaPtBr(value: number): string {
  const negative = value < 0;
  const tenths = Math.round(Math.abs(value) * 10);
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  return `${negative ? "-" : ""}${whole},${frac}`;
}

function formatTempo(timeSpentMs: number | undefined): string {
  if (!isPresentNumber(timeSpentMs)) return UNAVAILABLE;
  if (timeSpentMs < MS_PER_MINUTE) {
    return `${Math.round(timeSpentMs / 1000)} s`;
  }
  return `${Math.round(timeSpentMs / MS_PER_MINUTE)} min`;
}

function formatTaxa(retention: number | undefined): string {
  if (!isPresentNumber(retention)) return UNAVAILABLE;
  if (Number.isInteger(retention)) return `${retention}%`;
  return `${umaCasaPtBr(retention)}%`;
}

function formatSequencia(streak: number | undefined): string {
  if (!isPresentNumber(streak)) return UNAVAILABLE;
  if (streak === 1) return "1 dia";
  return `${streak} dias`;
}

function formatCartoes(cardsReviewed: number | undefined): string {
  if (!isPresentNumber(cardsReviewed)) return UNAVAILABLE;
  return String(cardsReviewed);
}

function formatDelta(delta: number | undefined): string | null {
  if (!isPresentNumber(delta)) return null;
  if (delta === 0) return "igual a ontem";
  const magnitude = Number.isInteger(Math.abs(delta))
    ? String(Math.abs(delta))
    : umaCasaPtBr(Math.abs(delta));
  const sign = delta > 0 ? "+" : "-";
  return `${sign}${magnitude} p.p. em relação a ontem`;
}

function contagemDaNota(
  counts: ReviewSessionSummary["ratingCounts"],
  key: Rating,
): number | undefined {
  if (!counts) return undefined;
  const value = counts[key];
  return isPresentNumber(value) ? value : undefined;
}

/**
 * Soma as quatro contagens que a leitura já trouxe. Esse total é o da própria
 * distribuição — não uma recontagem da fila que estava em memória.
 */
function totalDasNotas(
  counts: ReviewSessionSummary["ratingCounts"],
): number | undefined {
  if (!counts) return undefined;
  let sum = 0;
  for (const row of RATING_ROWS) {
    const value = contagemDaNota(counts, row.key);
    if (value === undefined) return undefined;
    sum += value;
  }
  return sum;
}

function Celebracao(): JSX.Element {
  return (
    <div className="relative overflow-hidden rounded-card border border-edge bg-surface px-4 py-8 text-center">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -left-8 -top-10 h-28 w-28 rounded-full bg-rate-easy/30 blur-2xl" />
        <div className="absolute -bottom-12 -right-8 h-32 w-32 rounded-full bg-select/30 blur-2xl" />
      </div>
      <PartyPopper
        aria-hidden="true"
        className="pointer-events-none relative mx-auto mb-3 h-8 w-8 text-ink"
      />
      <h1 className="relative text-2xl font-semibold text-ink">Sessão concluída</h1>
    </div>
  );
}

function Metrica({
  label,
  value,
  note,
  record,
}: {
  label: string;
  value: string;
  note?: string | null;
  record?: boolean;
}): JSX.Element {
  const unavailable = value === UNAVAILABLE;
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-card border border-edge bg-surface p-4">
      <p className="text-sm text-ink-muted">{label}</p>
      <p
        className={`break-words text-2xl font-semibold tabular-nums ${
          unavailable ? "text-ink-muted" : "text-ink"
        }`}
      >
        {value}
        {record ? (
          <span className="ml-2 align-middle text-sm font-medium text-ink-secondary">
            Recorde
          </span>
        ) : null}
      </p>
      {note ? <p className="text-sm text-ink-secondary">{note}</p> : null}
    </div>
  );
}

function Distribuicao({
  counts,
  total,
}: {
  counts: ReviewSessionSummary["ratingCounts"];
  total: number | undefined;
}): JSX.Element {
  return (
    <section
      aria-label="Distribuição das notas"
      className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-4"
    >
      <h2 className="text-base font-semibold text-ink">Distribuição das notas</h2>
      <p
        className={`text-sm tabular-nums ${
          total === undefined ? "text-ink-muted" : "text-ink"
        }`}
      >
        {total === undefined ? UNAVAILABLE : `${total} respostas`}
      </p>
      <ul className="flex flex-col gap-3">
        {RATING_ROWS.map((row) => {
          const count = contagemDaNota(counts, row.key);
          return (
            <li key={row.key} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className={`text-sm font-medium ${row.ink}`}>{row.label}</span>
                <span
                  className={`text-sm font-semibold tabular-nums ${
                    count === undefined ? "text-ink-muted" : row.ink
                  }`}
                >
                  {count === undefined ? UNAVAILABLE : String(count)}
                </span>
              </div>
              {count !== undefined && total !== undefined && total > 0 ? (
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-muted">
                  <div
                    className={`h-full rounded-full ${row.bar}`}
                    style={{ width: `${(count / total) * 100}%` }}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A mesma fita da montagem. Some por completo quando não há dia para mostrar. */
function ProximasRevisoes({ dias }: { dias: readonly AgendaDia[] }): JSX.Element | null {
  if (dias.length === 0) return null;
  return (
    <section
      aria-label="Próximas revisões"
      className="flex min-w-0 flex-col gap-3 rounded-card border border-edge bg-surface p-4"
    >
      <h2 className="text-base font-semibold text-ink">Próximas revisões</h2>
      <AgendaFita dias={dias} />
    </section>
  );
}

/** Some por completo quando a leitura não trouxe erro — sem título e sem lista vazia. */
function ParaReforcar({ lista }: { lista: readonly CartaoDeReforco[] }): JSX.Element | null {
  if (lista.length === 0) return null;
  return (
    <section
      aria-label="Para reforçar"
      className="flex min-w-0 flex-col gap-3 rounded-card border border-edge bg-surface p-4"
    >
      <h2 className="text-base font-semibold text-ink">Para reforçar</h2>
      <ul className="m-0 flex list-none flex-col p-0">
        {lista.map((item) => (
          <li
            key={item.id}
            className="flex min-w-0 flex-col gap-1 border-t border-edge py-3 first:border-t-0 first:pt-0 last:pb-0"
          >
            <span className="break-words text-sm font-medium text-ink">{item.rotulo}</span>
            <span data-reforco-assunto="" className="break-words text-sm text-ink-secondary">
              {item.assunto}
            </span>
            <span data-reforco-tipo="" className="text-sm text-ink-muted">
              {item.tipo}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Lido no corpo do componente, a cada render. Uma constante de módulo ou um
 * efeito perderia o dublê que a história instala antes de montar.
 */
function dispositivoPodeCompartilhar(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** Quatro medidas, uma por linha. Ausência continua "indisponível" — nunca 0. */
function textoParaCompartilhar(summary: ReviewSessionSummary): string {
  const linhas = [
    `Cartões revisados: ${formatCartoes(summary.cardsReviewed)}`,
    `Taxa de acerto: ${formatTaxa(summary.retention)}`,
    `Tempo gasto: ${formatTempo(summary.timeSpentMs)}`,
    `Sequência de dias de estudo: ${formatSequencia(summary.streak)}`,
  ];
  if (isPresentNumber(summary.retention)) {
    const variacao = formatDelta(summary.retentionDeltaVsYesterday);
    if (typeof variacao === "string") linhas.push(variacao);
  }
  if (summary.isStreakRecord === true && isPresentNumber(summary.streak)) {
    linhas.push("Recorde de sequência");
  }
  return linhas.join("\n");
}

function compartilharResumo(summary: ReviewSessionSummary): void {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") return;
  try {
    const resultado = navigator.share({
      title: "Sessão concluída",
      text: textoParaCompartilhar(summary),
    });
    void Promise.resolve(resultado).catch(() => undefined);
  } catch {
    // Cancelamento do usuário, ou o compartilhamento sumiu entre o render e o clique.
  }
}

export function ConclusaoSessao({
  summary,
  dias,
  assuntos,
  onVoltar,
  onAntecipar,
  onRevisarErros,
}: ConclusaoSessaoProps): JSX.Element {
  const podeCompartilhar = dispositivoPodeCompartilhar();
  const delta = isPresentNumber(summary.retention)
    ? formatDelta(summary.retentionDeltaVsYesterday)
    : null;
  const total = totalDasNotas(summary.ratingCounts);
  const lista = listaDeReforco(summary.reinforceCards, assuntos);

  return (
    <div className="relative isolate flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden md:h-auto md:max-h-none md:gap-6 md:overflow-visible md:px-4 md:py-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-5 top-7 -z-10 h-[280px] w-[340px] rounded-full bg-brand/25 blur-3xl md:hidden"
      />
      <header className="flex shrink-0 items-center justify-between px-5 pt-[max(0.75rem,env(safe-area-inset-top))] md:hidden">
        <button
          type="button"
          aria-label="Fechar"
          data-acao="fechar"
          onClick={onVoltar}
          className={BOTAO_CABECALHO}
        >
          <X aria-hidden="true" size={18} />
        </button>
        {podeCompartilhar ? (
          <button
            type="button"
            aria-label="Compartilhar"
            data-acao="compartilhar"
            onClick={() => compartilharResumo(summary)}
            className={BOTAO_CABECALHO}
          >
            <Share aria-hidden="true" size={18} />
          </button>
        ) : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 py-6 md:flex-none md:overflow-visible md:p-0">
        <Celebracao />
        <div className="grid grid-cols-2 items-start gap-3 md:grid-cols-4">
          <Metrica label="Cartões revisados" value={formatCartoes(summary.cardsReviewed)} />
          <Metrica label="Taxa de acerto" value={formatTaxa(summary.retention)} note={delta} />
          <Metrica label="Tempo gasto" value={formatTempo(summary.timeSpentMs)} />
          <Metrica
            label="Sequência de dias de estudo"
            value={formatSequencia(summary.streak)}
            record={summary.isStreakRecord === true && isPresentNumber(summary.streak)}
          />
        </div>
        <Distribuicao counts={summary.ratingCounts} total={total} />
        <ProximasRevisoes dias={dias} />
        <ParaReforcar lista={lista} />
      </div>
      <div
        data-conclusao-acoes="fixas"
        className="shrink-0 border-t border-edge bg-canvas px-5 pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:border-0 md:bg-transparent md:p-0"
      >
        <ConclusaoAcoes
          antecipar={contagemDeAmanha(dias)}
          erros={lista.map((item) => item.id)}
          onVoltar={onVoltar}
          onAntecipar={onAntecipar}
          onRevisarErros={onRevisarErros}
        />
      </div>
    </div>
  );
}
